import { SupportMessageType, SupportMessageStatus } from '../types/atendimentoEnums';
import { SupportMessage } from '../types/supportMessage';

export const PENDING_RETRY_AFTER_MS = 60_000;
export const WHATSAPP_CUSTOMER_CARE_WINDOW_ERROR_CODE = '131047';

export interface SupportMessageDeliveryView {
  error: string | null;
  errorCode: string | null;
  outcomeUnknown: boolean;
  sourceId: string | null;
  retryLastAttemptAt: string | null;
}

const EMPTY_DELIVERY: SupportMessageDeliveryView = {
  error: null,
  errorCode: null,
  outcomeUnknown: false,
  sourceId: null,
  retryLastAttemptAt: null,
};

function readStatus(status?: string | null): string {
  return (status ?? '').trim();
}

function readOptionalString(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim())
  {
    return null;
  }

  return value;
}

export function parseSupportMessageDelivery(
  contentAttributes?: string | null,
): SupportMessageDeliveryView {
  if (!contentAttributes)
  {
    return EMPTY_DELIVERY;
  }

  try
  {
    const parsed = JSON.parse(contentAttributes) as Record<string, unknown>;
    return {
      error: readOptionalString(parsed.deliveryError),
      errorCode: readOptionalString(parsed.deliveryErrorCode),
      outcomeUnknown: parsed.deliveryOutcomeUnknown === true,
      sourceId: readOptionalString(parsed.deliverySourceId),
      retryLastAttemptAt: readOptionalString(parsed.deliveryRetryLastAttemptAt),
    };
  }
  catch
  {
    return EMPTY_DELIVERY;
  }
}

export function isPublicOutgoingMessage(message: SupportMessage): boolean {
  return !message.isPrivate && message.messageType === SupportMessageType.Outgoing;
}

export function canRetrySupportMessage(message: SupportMessage, now = Date.now()): boolean {
  if (!isPublicOutgoingMessage(message))
  {
    return false;
  }

  const status = readStatus(message.status);
  const delivery = parseSupportMessageDelivery(message.contentAttributes);
  if (delivery.errorCode === WHATSAPP_CUSTOMER_CARE_WINDOW_ERROR_CODE)
  {
    return false;
  }

  if (status === SupportMessageStatus.Failed)
  {
    return true;
  }

  if (status !== SupportMessageStatus.Pending)
  {
    return false;
  }

  const lastAttempt = delivery.retryLastAttemptAt
    ? Date.parse(delivery.retryLastAttemptAt)
    : Date.parse(message.createdAt);
  if (!Number.isFinite(lastAttempt))
  {
    return false;
  }

  return now - lastAttempt >= PENDING_RETRY_AFTER_MS;
}

export function isFailedSupportMessage(message: SupportMessage): boolean {
  return readStatus(message.status) === SupportMessageStatus.Failed;
}

export function isPendingSupportMessage(message: SupportMessage): boolean {
  return readStatus(message.status) === SupportMessageStatus.Pending;
}
